import { useState, useEffect, useMemo, useCallback, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../shell/shared';
import { QuickPatientDrawer } from './QuickPatientDrawer';
import {
  AddToQueueDrawer,
  type AddToQueuePrefill,
  type AddToQueueData,
} from '../queue/AddToQueueDrawer';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { useProviderScope } from '../../auth/useProviderScope';
import { ensureVisitThenCreateEncounter } from '../../services/encounter-service';
import {
  usePatientTabs,
  getPatientColor,
  getShortLabel,
  getInitialsShort,
} from '../../shell/PatientTabsContext';
import {
  qAppointmentProviders,
  qAppointmentServicesWithCategory,
  qAppointmentStatuses,
  qPatientsList,
  qFrontDeskSettings,
  qFrontDeskProviderSettings,
  qFrontDeskServiceSettings,
  qQueuedAppointmentIds,
  qNextQueueNumber,
  qPatientContact,
  qPatientCommunicationPreferences,
  qNotificationJobsByPatient,
  qAppointmentWhatsappResponsesByAppointment,
  qAvailableSlotsForBooking,
  qAppointmentsByDateRange,
  qQueueToday,
  computeProviderDelayMinutes,
  type QueueEntryRow,
  findAppointmentConflicts,
  describeAppointmentConflict,
  type AppointmentRow,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
  type AppointmentStatusRow,
  type PatientListRow,
  type FrontDeskSettingsRow,
  type FrontDeskProviderSettingRow,
  type FrontDeskServiceSettingRow,
  type QueuedAppointmentRow,
  type PatientContactRow,
  type PatientCommunicationPreferencesClientRow,
  type NotificationJobClientRow,
  type AppointmentWhatsAppResponseClientRow,
  type AppointmentSlotWithBookingRow,
  qAppConfig,
  getAppointmentBookedDefault,
  appointmentBookedReference,
  resolveWhatsAppToggleState,
  whatsAppToggleHint,
  WEB_ADMIN_APPOINTMENT_SOURCE,
  type AppConfigRow,
  filterBookableServices,
} from '@helios/data-access';
import { useLaboratorySettings } from '../../hooks/useLaboratory';
import { SendWhatsAppReminderAction } from '../../components/SendWhatsAppReminderAction';
import { SendWhatsAppFreeFormAction } from '../../components/SendWhatsAppFreeFormAction';
import { SendWhatsAppBookingFlowAction } from '../../components/SendWhatsAppBookingFlowAction';
import { AppointmentResponseBadge } from '../../components/AppointmentResponseBadge';
import { isAppointmentReminderReference } from '../../lib/appointment-reminder';
import { setWhatsAppOptIn, whatsAppOptInState } from '../../lib/patient-communication-preferences';
import { useAppointmentBookedNotifier } from '../../notifications/AppointmentBookedStatus';

// ─── Types ───

export interface BookDrawerPrefill {
  appointmentId?: string; // when set, drawer is in edit mode
  date?: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
  /** PRD.234 — optional end time so slot-driven prefill can pin duration. */
  endTime?: string; // HH:mm
  /** PRD.234 — explicit duration in minutes (overrides service typical duration). */
  durationMinutes?: number;
  patientId?: string; // lock patient selection when set
  providerId?: string;
  serviceId?: string;
  statusId?: string;
  notes?: string;
  /** PRD.234 — when set, drawer preselects this slot card. */
  appointmentSlotId?: string;
}

interface BookAppointmentDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  prefill?: BookDrawerPrefill;
  onBooked?: (appt: BookedAppointment) => void;
  readOnly?: boolean;
}

export interface BookedAppointment {
  id: string;
  patientId: string;
  patientName: string;
  providerId: string;
  providerName: string;
  serviceId: string;
  serviceName: string;
  category: string;
  date: string;
  startTime: string;
  durationMins: number;
  status: string;
  notes: string;
}

// ─── Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-sm)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: 'var(--sp-2)',
};

const inputStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'auto',
};

// ─── Helpers ───

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── Component ───

export function BookAppointmentDrawer({
  open,
  onOpenChange,
  brandTokens,
  prefill,
  onBooked,
  readOnly: readOnlyProp,
}: BookAppointmentDrawerProps) {
  const { user, claims } = useAuth();
  const { db } = usePowerSyncDb();
  const orgId = claims?.activeOrg ?? '';
  // PRD.293 — for scoped doctors, new appointments default to and are locked to
  // the doctor's own provider.
  const providerScope = useProviderScope();

  // ── PowerSync queries ──
  const { data: allProviderRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: allServiceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientListRow>(
    qPatientsList({ userId: user?.uid ?? '', sort: 'name-az' }),
  );

  // Front desk booking settings
  const { data: fdSettingsRows = [] } =
    usePowerSyncQuery<FrontDeskSettingsRow>(qFrontDeskSettings());
  const { data: fdProviderRows = [] } = usePowerSyncQuery<FrontDeskProviderSettingRow>(
    qFrontDeskProviderSettings(),
  );
  const { data: fdServiceRows = [] } = usePowerSyncQuery<FrontDeskServiceSettingRow>(
    qFrontDeskServiceSettings(),
  );

  const fdSettings = fdSettingsRows[0] ?? null;

  // ── Derived: filter providers/services by front desk settings ──
  // A missing settings row means the item was never saved into
  // booking_frontdesk_(provider|service)_settings — it should be treated as
  // enabled by default, matching how FrontDeskBookingSettingsPage renders its
  // toggles. Otherwise services/providers added after the last settings save
  // are invisible here even though the settings UI shows them enabled.
  const providers = useMemo(() => {
    const active = allProviderRows.filter((p) => p.active == 1);
    if (fdProviderRows.length === 0) return active;
    const settingById = new Map(fdProviderRows.map((r) => [r.provider_id, r]));
    return active.filter((p) => {
      const row = settingById.get(p.id);
      return row ? row.enabled == 1 : true;
    });
  }, [allProviderRows, fdProviderRows]);

  // PRD.329 follow-up — Laboratory Settings may hide lab tests from booking
  // (default off). A prefilled service is always kept so rebooking never blanks it.
  const { hideLabTestsFromBooking } = useLaboratorySettings();
  const services = useMemo(() => {
    const active = filterBookableServices(
      allServiceRows.filter((s) => s.active == 1),
      hideLabTestsFromBooking,
      [prefill?.serviceId],
    );
    if (fdServiceRows.length === 0) return active;
    const settingById = new Map(fdServiceRows.map((r) => [r.service_id, r]));
    return active.filter((s) => {
      const row = settingById.get(s.id);
      return row ? row.enabled == 1 : true;
    });
  }, [allServiceRows, fdServiceRows, hideLabTestsFromBooking, prefill?.serviceId]);

  const statuses = useMemo(() => statusRows.filter((s) => s.active == 1), [statusRows]);

  // Build a map of service_id → duration override from front desk settings
  const durationOverrides = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of fdServiceRows) {
      if (r.service_id && r.duration_override_mins != null && r.duration_override_mins > 0) {
        map.set(r.service_id, r.duration_override_mins);
      }
    }
    return map;
  }, [fdServiceRows]);

  // Default status from front desk settings
  const defaultStatusId = fdSettings?.default_appointment_status_id ?? '';

  // Form state
  const [patientId, setPatientId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [providerId, setProviderId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [notes, setNotes] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [saving, setSaving] = useState(false);

  // PRD.234 — slot selection
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [manualBookingEnabled, setManualBookingEnabled] = useState(false);

  // Nested drawers
  const [quickPatientOpen, setQuickPatientOpen] = useState(false);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);

  // Read-only mode with unlock
  const [unlocked, setUnlocked] = useState(false);
  const isLocked = !!readOnlyProp && !unlocked;

  const lockedPatientId = prefill?.patientId ?? '';
  const isEditMode = !!prefill?.appointmentId;

  // ── Queue: check if this appointment is already queued today ──
  const queueDate = todayStr();
  const { data: queuedRows = [] } = usePowerSyncQuery<QueuedAppointmentRow>(
    qQueuedAppointmentIds(queueDate),
  );
  const { data: maxNumRows = [] } = usePowerSyncQuery<{ max_num: number | null }>(
    qNextQueueNumber(queueDate),
  );
  const isAlreadyQueued =
    isEditMode && queuedRows.some((r) => r.appointment_id === prefill?.appointmentId);

  // Build prefill for the nested Add to Queue drawer
  const queueDrawerPrefill: AddToQueuePrefill | null = useMemo(() => {
    if (!prefill?.appointmentId || !patientId) return null;
    const patient = patientRows.find((p) => p.id === patientId);
    return {
      patientId,
      patientName: patient?.full_name ?? 'Unknown',
      appointmentId: prefill.appointmentId,
      providerId: providerId || undefined,
      serviceId: serviceId || undefined,
      notes: notes || undefined,
    };
  }, [prefill, patientId, patientRows, providerId, serviceId, notes]);

  // ── Reminders (PRD.221): patient contact + opt-in + latest reminder job ──
  // patientId is '' until a patient is selected — empty params yield empty
  // results, so these watches are cheap no-ops outside edit mode.
  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId),
  );
  const { data: commPrefRows = [] } = usePowerSyncQuery<PatientCommunicationPreferencesClientRow>(
    qPatientCommunicationPreferences(patientId),
  );
  const { data: reminderJobRows = [] } = usePowerSyncQuery<NotificationJobClientRow>(
    qNotificationJobsByPatient(patientId),
  );
  const reminderPhone = contactRows[0]?.mobile_number ?? null;
  // Require an explicit, non-revoked opt-in — a missing prefs row = not opted in.
  const reminderOptedIn =
    commPrefRows[0]?.whatsapp_opt_in === 1 && !commPrefRows[0]?.whatsapp_opt_out_at;

  // ── PRD.299 — WhatsApp booking confirmation ──
  const {
    notifyAppointmentBooked,
    notifyAppointmentReminder,
    isConfigured: whatsAppBookedConfigured,
  } = useAppointmentBookedNotifier();
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const whatsAppClinicDefault = getAppointmentBookedDefault(appConfigRows[0] ?? null);
  // Same opt-in semantics the backend enforces: an explicit, non-revoked
  // opt-in. A missing prefs row is NOT consent.
  const whatsAppToggle = resolveWhatsAppToggleState({
    optInState: whatsAppOptInState(commPrefRows[0] ?? null),
    hasMobileNumber: Boolean(contactRows[0]?.mobile_number),
    clinicDefault: whatsAppClinicDefault,
  });
  const [optingIn, setOptingIn] = useState(false);
  // Only the USER'S explicit override is stored; null means "follow the
  // resolved default". Seeding state from `whatsAppToggle.checked` instead was
  // a race: contacts/preferences arrive asynchronously from PowerSync, so the
  // first render resolves to disabled+unchecked, the state latched false, and
  // it never re-synced once the data landed — leaving the box silently
  // unticked for an eligible patient. Deriving each render has no such window.
  const [whatsAppChoice, setWhatsAppChoice] = useState<boolean | null>(null);
  // Reset the override when the patient changes so the next patient starts
  // from the clinic default rather than inheriting a previous choice.
  const [choiceForPatient, setChoiceForPatient] = useState<string | null>(null);
  if (choiceForPatient !== (patientId ?? null)) {
    setChoiceForPatient(patientId ?? null);
    setWhatsAppChoice(null);
  }
  const sendWhatsAppConfirmation = whatsAppChoice ?? whatsAppToggle.checked;
  // PRD.299/300 — `handleSave` is memoized over a dependency list that omits
  // these values (they change on every PowerSync query settle). Reading them
  // through a ref keeps the click handler on the LATEST values instead of the
  // first-render ones. Without this the closure captured the pre-load state —
  // hasMobile=false, optIn=not-set, enabled=false — so the confirmation was
  // silently skipped even though the checkbox was ticked on screen.
  const whatsAppLatestRef = useRef({
    toggleEnabled: false,
    sendConfirmation: false,
    notifyBooked: notifyAppointmentBooked,
    notifyReminder: notifyAppointmentReminder,
  });
  whatsAppLatestRef.current = {
    toggleEnabled: whatsAppToggle.enabled,
    sendConfirmation: sendWhatsAppConfirmation,
    notifyBooked: notifyAppointmentBooked,
    notifyReminder: notifyAppointmentReminder,
  };

  const reminderJob = useMemo(() => {
    if (!prefill?.appointmentId) return null;
    // PRD.300 — references are dated, so match by prefix (the legacy undated
    // form is still recognized for historical rows). Rows arrive newest-first.
    const apptId = prefill.appointmentId;
    return (
      reminderJobRows.find((j) => isAppointmentReminderReference(j.client_reference, apptId)) ??
      null
    );
  }, [prefill?.appointmentId, reminderJobRows]);

  // PRD.224 — patient WhatsApp reply for this appointment (latest first).
  const { data: responseRows = [] } = usePowerSyncQuery<AppointmentWhatsAppResponseClientRow>(
    qAppointmentWhatsappResponsesByAppointment(prefill?.appointmentId ?? ''),
  );
  const latestResponse = responseRows[0] ?? null;

  const navigate = useNavigate();
  const { openPatientTab } = usePatientTabs();
  const [startingEncounter, setStartingEncounter] = useState(false);

  const handleStartEncounter = useCallback(async () => {
    if (!db || !patientId || !orgId) return;
    setStartingEncounter(true);
    try {
      const result = await ensureVisitThenCreateEncounter({
        db,
        orgId,
        patientId,
        appointmentId: prefill?.appointmentId ?? null,
        providerId: providerId || null,
      });

      const patient = patientRows.find((p) => p.id === patientId);
      const name = patient?.full_name ?? 'Unknown';
      openPatientTab({
        id: patientId,
        label: getShortLabel(name),
        initials: getInitialsShort(name),
        color: getPatientColor(patientId),
        photoUrl: null,
      });

      onOpenChange(false);
      navigate(`/patient/${patientId}/encounters/${result.encounterId}`);
    } catch (err) {
      console.error('[BookAppointmentDrawer] Start encounter failed:', err);
    } finally {
      setStartingEncounter(false);
    }
  }, [
    db,
    patientId,
    orgId,
    prefill,
    providerId,
    patientRows,
    openPatientTab,
    onOpenChange,
    navigate,
  ]);

  const handleOpenQueueDrawer = useCallback(() => {
    if (!prefill?.appointmentId || isAlreadyQueued) return;
    setQueueDrawerOpen(true);
  }, [prefill, isAlreadyQueued]);

  const handleAddToQueue = useCallback(
    async (data: AddToQueueData) => {
      if (!db) return;
      try {
        const maxNum = maxNumRows[0]?.max_num ?? 0;
        const nextNum = maxNum + 1;
        const queueNumber = `Q-${String(nextNum).padStart(3, '0')}`;
        const now = new Date().toISOString();
        const status = data.arrived ? 'waiting' : 'expected';

        await db.execute(
          `INSERT INTO queue_entries
            (id, org_id, patient_id, appointment_id, queue_number, position, queue_date,
             source, status, priority, provider_id, service_id, notes, tags,
             arrived_at, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId || null,
            data.patientId || null,
            data.appointmentId || null,
            queueNumber,
            nextNum,
            queueDate,
            'appointment',
            status,
            data.priority || 'normal',
            data.providerId || null,
            data.serviceId || null,
            data.notes || null,
            data.tags.length > 0 ? JSON.stringify(data.tags) : null,
            data.arrived ? now : null,
            now,
            now,
          ],
        );
        setQueueDrawerOpen(false);
        setToast('Added to queue');
        setToastType('success');
      } catch (err) {
        console.error('Add to queue failed:', err);
        setToast('Failed to add to queue');
        setToastType('error');
      }
    },
    [db, orgId, maxNumRows, queueDate],
  );

  // Reset form on open
  useEffect(() => {
    if (open) {
      setPatientId(lockedPatientId);
      setPatientSearch('');
      setShowPatientDropdown(false);
      setDate(prefill?.date ?? todayStr());
      setStartTime(prefill?.startTime ?? '');
      // PRD.293 — scoped doctors default to their own provider (prefill wins if set).
      setProviderId(
        prefill?.providerId ?? (providerScope.scoped ? (providerScope.providerId ?? '') : ''),
      );
      setServiceId(prefill?.serviceId ?? '');
      setStatusId(prefill?.statusId ?? defaultStatusId);
      setNotes(prefill?.notes ?? '');
      setToast('');
      setUnlocked(false);
      setSelectedSlotId(prefill?.appointmentSlotId ?? null);
      setManualBookingEnabled(false);
    }
  }, [
    open,
    prefill,
    defaultStatusId,
    lockedPatientId,
    providerScope.scoped,
    providerScope.providerId,
  ]);

  // PRD.234 — fetch available slots when provider+service+date are picked.
  // Empty params yield empty results; safe to subscribe unconditionally.
  const { data: slotRows = [] } = usePowerSyncQuery<AppointmentSlotWithBookingRow>(
    qAvailableSlotsForBooking(providerId, serviceId, date),
  );
  const availableSlots = useMemo(
    () =>
      slotRows.filter((s) => {
        const cap = s.capacity ?? 1;
        const booked = s.booked_count ?? 0;
        return booked < cap;
      }),
    [slotRows],
  );
  // When the user picks a service/provider/date that has slots, default to
  // slot-based booking; surface the manual escape hatch only when there are
  // no available slots OR when the user explicitly enables it.
  const hasSlotsForSelection = providerId && serviceId && date && availableSlots.length > 0;
  const showSlotPicker = hasSlotsForSelection && !manualBookingEnabled;

  const handleSelectSlot = useCallback((slot: AppointmentSlotWithBookingRow) => {
    if (!slot.start_time || !slot.end_time) return;
    setSelectedSlotId(slot.id);
    setManualBookingEnabled(false);
    // Slot timestamps are stored as wall-clock-as-UTC (see
    // generateSlotDrafts). Read with UTC accessors so booking sets
    // "08:00" regardless of the user's local timezone.
    const start = new Date(slot.start_time);
    const hh = String(start.getUTCHours()).padStart(2, '0');
    const mm = String(start.getUTCMinutes()).padStart(2, '0');
    setStartTime(`${hh}:${mm}`);
  }, []);

  // When locked patient rows load, set search text
  useEffect(() => {
    if (lockedPatientId && patientRows.length > 0) {
      const p = patientRows.find((r) => r.id === lockedPatientId);
      if (p) {
        setPatientId(p.id);
        setPatientSearch(p.full_name ?? '');
      }
    }
  }, [lockedPatientId, patientRows]);

  // Derived duration from selected service (with override)
  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId],
  );
  const durationMins = useMemo(() => {
    // PRD.234 — slot-driven prefill pins the duration explicitly.
    if (prefill?.durationMinutes && prefill.durationMinutes > 0) return prefill.durationMinutes;
    if (!serviceId) return 0;
    const override = durationOverrides.get(serviceId);
    if (override) return override;
    return selectedService?.typical_duration_mins ?? 0;
  }, [prefill?.durationMinutes, serviceId, durationOverrides, selectedService]);

  // PRD.234 — client-side overlap detection, mirroring syncWrite's beforeWrite
  // guard. Without this the server rejects the upload in the BACKGROUND, long
  // after the drawer reported success, and the user is never told. Load the
  // candidate day plus the one before, so a booking spilling past midnight is
  // still caught.
  const conflictPrevDate = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
    if (!m) return date ?? '';
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [date]);
  const { data: conflictCandidates = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentsByDateRange(conflictPrevDate, date || conflictPrevDate),
  );
  const statusTypeById = useMemo(() => new Map(statuses.map((st) => [st.id, st.type])), [statuses]);
  const appointmentConflicts = useMemo(() => {
    if (!date || !(durationMins > 0)) return [];
    return findAppointmentConflicts(
      {
        id: prefill?.appointmentId ?? null,
        providerId: providerId || null,
        patientId: patientId || null,
        date,
        startTime: startTime || '09:00',
        durationMinutes: durationMins || 30,
      },
      conflictCandidates.map((a) => ({
        id: a.id,
        provider_id: a.provider_id,
        patient_id: a.patient_id,
        date: a.date,
        start_time: a.start_time,
        duration_minutes: a.duration_minutes,
        // Unresolvable status_id stays null → treated as blocking, matching
        // the server's LEFT JOIN.
        status_type: a.status_id ? (statusTypeById.get(a.status_id) ?? null) : null,
      })),
    );
  }, [
    conflictCandidates,
    statusTypeById,
    date,
    startTime,
    durationMins,
    providerId,
    patientId,
    prefill?.appointmentId,
  ]);
  const conflictMessage = useMemo(() => {
    const first = appointmentConflicts[0];
    if (!first) return null;
    return describeAppointmentConflict(first, {
      providerName:
        providers.find((pr) => pr.id === providerId)?.display_name ??
        providers.find((pr) => pr.id === providerId)?.full_name ??
        null,
      patientName: patientRows.find((pt) => pt.id === patientId)?.full_name ?? null,
    });
  }, [appointmentConflicts, providers, providerId, patientRows, patientId]);
  // PRD.235 — how far behind is THIS PROVIDER running right now? Measured from
  // today's queue: `serving_at` vs each appointment's scheduled start. Scoped
  // to the provider, because "we're running late" is about the doctor the
  // patient is waiting for, not the clinic as a whole.
  const { data: providerQueueRows = [] } = usePowerSyncQuery<
    QueueEntryRow & { appointment_start_time?: string | null }
  >(qQueueToday(date, providerId || undefined));
  const providerDelayMinutes = useMemo(
    () =>
      computeProviderDelayMinutes({
        date,
        now: new Date(),
        // Strongest signal: if this patient's own slot has passed, quote how
        // long THEY have been waiting.
        recipientStartTime: startTime || null,
        queue: providerQueueRows.map((q) => ({
          appointmentStartTime: q.appointment_start_time ?? null,
          servingAt: q.serving_at ?? null,
          calledAt: q.called_at ?? null,
          status: q.status ?? null,
        })),
        appointments: conflictCandidates
          .filter((a) => a.date === date && a.provider_id === providerId)
          .map((a) => ({
            startTime: a.start_time,
            started: providerQueueRows.some(
              (q) => q.appointment_id === a.id && (q.serving_at || q.called_at || q.done_at),
            ),
          })),
      }) ?? undefined,
    [date, startTime, providerId, providerQueueRows, conflictCandidates],
  );

  // PRD.299 — has the booking confirmation already been sent? The endpoint is
  // idempotent on this reference, so a second send is a no-op; surface that in
  // the UI rather than letting the button look broken.
  const bookedJob = useMemo(() => {
    if (!prefill?.appointmentId) return null;
    const ref = appointmentBookedReference(prefill.appointmentId);
    return reminderJobRows.find((j) => j.client_reference === ref) ?? null;
  }, [prefill?.appointmentId, reminderJobRows]);

  // Stale-closure safe: handleSave's dep list omits this, so read via ref.
  const conflictRef = useRef<string | null>(null);
  conflictRef.current = conflictMessage;

  // Selected patient
  const selectedPatient = useMemo(
    () => patientRows.find((p) => p.id === patientId),
    [patientRows, patientId],
  );

  // Patient search results
  const patientResults = useMemo(() => {
    if (!patientSearch.trim()) return patientRows;
    const q = patientSearch.toLowerCase();
    return patientRows.filter(
      (p) =>
        (p.full_name ?? '').toLowerCase().includes(q) ||
        (p.patient_number ?? '').toLowerCase().includes(q),
    );
  }, [patientRows, patientSearch]);

  const canSave = patientId && date && providerId && serviceId;

  const handleSave = useCallback(async () => {
    if (!canSave || !db) return;
    const provider = providers.find((p) => p.id === providerId);
    const service = services.find((s) => s.id === serviceId);
    const patient = patientRows.find((p) => p.id === patientId);
    const statusRow = statuses.find((s) => s.id === statusId);
    if (!provider || !service || !patient) return;

    // PRD.234 — refuse the double-booking HERE, with a specific message, rather
    // than letting syncWrite reject the upload minutes later where nobody sees
    // it. Read through a ref: handleSave's dep list omits the conflict state.
    const conflict = conflictRef.current;
    if (conflict) {
      setToast(conflict);
      setToastType('error');
      return;
    }

    setSaving(true);
    // Set only on the create path — a reschedule must never re-confirm.
    let createdAppointmentId: string | null = null;
    try {
      const now = new Date().toISOString();
      const finalDuration = durationMins || 30;

      if (isEditMode) {
        // ── UPDATE existing appointment ──
        await db.execute(
          `UPDATE appointments
           SET patient_id = ?, provider_id = ?, service_id = ?, status_id = ?, category_id = ?,
               date = ?, start_time = ?, duration_minutes = ?, notes = ?,
               appointment_slot_id = ?, updated_at = ?
           WHERE id = ?`,
          [
            patient.id,
            provider.id,
            service.id,
            statusRow?.id ?? '',
            service.category_id ?? null,
            date,
            startTime || '09:00',
            finalDuration,
            notes || null,
            selectedSlotId,
            now,
            prefill!.appointmentId,
          ],
        );
      } else {
        // ── INSERT new appointment ──
        const apptId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO appointments
            (id, org_id, patient_id, provider_id, service_id, status_id, category_id, date, start_time, duration_minutes, notes, appointment_slot_id, source, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            apptId,
            orgId,
            patient.id,
            provider.id,
            service.id,
            statusRow?.id ?? '',
            service.category_id ?? null,
            date,
            startTime || '09:00',
            finalDuration,
            notes || null,
            selectedSlotId,
            // PRD.299 — booking provenance. Only WEB_ADMIN appointments get an
            // APPOINTMENT_BOOKED confirmation; legacy NULL rows never do.
            WEB_ADMIN_APPOINTMENT_SOURCE,
            now,
            now,
          ],
        );
        createdAppointmentId = apptId;
      }

      const booked: BookedAppointment = {
        id: isEditMode ? prefill!.appointmentId! : crypto.randomUUID(),
        patientId: patient.id,
        patientName: patient.full_name ?? '',
        providerId: provider.id,
        providerName: provider.display_name || provider.full_name || '',
        serviceId: service.id,
        serviceName: service.name ?? '',
        category: service.category_name ?? '',
        date,
        startTime: startTime || '09:00',
        durationMins: finalDuration,
        status: statusRow?.label ?? 'Scheduled',
        notes,
      };

      setToast(isEditMode ? 'Appointment updated' : `Appointment booked for ${patient.full_name}`);
      setToastType('success');

      // Read through the ref — see whatsAppLatestRef above.
      const wa = whatsAppLatestRef.current;

      // PRD.299 — request the WhatsApp confirmation. Deliberately NOT awaited:
      // the appointment is already saved and must never wait on, or be failed
      // by, a notification. Status is surfaced by AppointmentBookedStatus.
      if (createdAppointmentId && wa.sendConfirmation && wa.toggleEnabled) {
        wa.notifyBooked(createdAppointmentId, patient.full_name ?? 'patient');
      }

      // PRD.300 path B — same-day reminder, requested for EVERY created
      // appointment. Independent of the confirmation toggle above: the backend
      // owns eligibility (today? past 06:00? lead time? opted in?), and a
      // front-desk choice to skip the booking confirmation must not silently
      // suppress the patient's reminder as well.
      if (createdAppointmentId) {
        wa.notifyReminder(createdAppointmentId);
      }

      onBooked?.(booked);
      setTimeout(() => onOpenChange(false), 800);
    } catch (err) {
      console.error(`${isEditMode ? 'Update' : 'Create'} appointment failed:`, err);
      setToast(`Failed to ${isEditMode ? 'update' : 'create'} appointment. Please try again.`);
      setToastType('error');
    } finally {
      setSaving(false);
    }
  }, [
    canSave,
    db,
    orgId,
    isEditMode,
    prefill,
    providers,
    services,
    patientRows,
    statuses,
    providerId,
    serviceId,
    patientId,
    statusId,
    durationMins,
    date,
    startTime,
    notes,
    selectedSlotId,
    onBooked,
    onOpenChange,
  ]);

  const handleQuickPatientCreated = (newPatient: { id: string; name: string }) => {
    setPatientId(newPatient.id);
    setPatientSearch(newPatient.name);
    setShowPatientDropdown(false);
  };

  const handleSelectPatient = (p: PatientListRow) => {
    setPatientId(p.id);
    setPatientSearch(p.full_name ?? '');
    setShowPatientDropdown(false);
  };

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={isLocked ? 'View Appointment' : isEditMode ? 'Edit Appointment' : 'Book a Patient'}
        description={
          isLocked
            ? 'Appointment details (read-only)'
            : isEditMode
              ? 'Update appointment details'
              : 'Schedule a new appointment'
        }
        width={500}
        portalStyle={brandTokens as CSSProperties}
        footer={
          isLocked ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button variant="gradient" size="lg" onClick={() => setUnlocked(true)}>
                Unlock to Edit
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="gradient"
                size="lg"
                onClick={handleSave}
                disabled={!canSave || saving || Boolean(conflictMessage)}
              >
                {saving
                  ? isEditMode
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Update Appointment'
                    : 'Create Appointment'}
              </Button>
            </div>
          )
        }
      >
        <div
          style={{
            padding: 'var(--sp-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* Toast */}
          {toast && (
            <div
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                background: toastType === 'success' ? '#dcfce7' : '#fef2f2',
                color: toastType === 'success' ? '#15803d' : '#dc2626',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {toast}
            </div>
          )}

          {/* ── Section: Patient ── */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-3)',
                paddingBottom: 'var(--sp-2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <NavIcon name="user" />
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Patient
              </span>
            </div>

            {/* Selected patient chip or search */}
            {selectedPatient && (isLocked || !showPatientDropdown) ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface-2)',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-full)',
                    background: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#475569',
                    flexShrink: 0,
                  }}
                >
                  {getInitials(selectedPatient.full_name ?? '')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                    }}
                  >
                    {selectedPatient.full_name}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {selectedPatient.patient_number ?? selectedPatient.id}
                  </div>
                </div>
                {!lockedPatientId && !isLocked && (
                  <button
                    type="button"
                    onClick={() => {
                      setPatientId('');
                      setPatientSearch('');
                      setShowPatientDropdown(true);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 4,
                      fontSize: 16,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      pointerEvents: 'none',
                    }}
                  >
                    <NavIcon name="search" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search patient by name or ID..."
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      setShowPatientDropdown(true);
                    }}
                    onFocus={() => setShowPatientDropdown(true)}
                    style={{ ...inputStyle, paddingLeft: 40 }}
                  />
                </div>

                {/* Dropdown */}
                {showPatientDropdown && (
                  <>
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 1 }}
                      onClick={() => setShowPatientDropdown(false)}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        maxHeight: 220,
                        overflowY: 'auto',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 2,
                      }}
                    >
                      {/* New patient CTA */}
                      <button
                        type="button"
                        onClick={() => {
                          setShowPatientDropdown(false);
                          setQuickPatientOpen(true);
                        }}
                        style={{
                          width: '100%',
                          padding: 'var(--sp-3) var(--sp-4)',
                          background: 'none',
                          border: 'none',
                          borderBottom: '1px solid var(--border)',
                          textAlign: 'left',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--color-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-2)',
                        }}
                      >
                        <NavIcon name="plus" />
                        New patient
                      </button>

                      {patientResults.length === 0 ? (
                        <div
                          style={{
                            padding: 'var(--sp-4)',
                            textAlign: 'center',
                            color: 'var(--text-muted)',
                            fontSize: 'var(--font-size-sm)',
                          }}
                        >
                          No patients found
                        </div>
                      ) : (
                        patientResults.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => handleSelectPatient(p)}
                            style={{
                              width: '100%',
                              padding: 'var(--sp-2) var(--sp-4)',
                              background: 'none',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--sp-3)',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background =
                                'var(--bg-surface-2)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = 'none';
                            }}
                          >
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 'var(--radius-full)',
                                background: '#dbeafe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#475569',
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(p.full_name ?? '')}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 'var(--font-size-sm)',
                                  fontWeight: 'var(--font-weight-medium)',
                                  color: 'var(--text)',
                                }}
                              >
                                {p.full_name}
                              </div>
                              <div
                                style={{
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--text-muted)',
                                }}
                              >
                                {p.patient_number ?? p.id}
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Section: Appointment Details ── */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-3)',
                paddingBottom: 'var(--sp-2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <NavIcon name="calendar" />
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Appointment Details
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              {/* Date + Time row */}
              <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setSelectedSlotId(null);
                    }}
                    disabled={isLocked}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => {
                      setStartTime(e.target.value);
                      if (selectedSlotId) setSelectedSlotId(null);
                    }}
                    disabled={isLocked || !!showSlotPicker}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* PRD.234 — Available slots */}
              {!isLocked && providerId && serviceId && date && (
                <AvailableSlotsSection
                  slots={availableSlots}
                  selectedSlotId={selectedSlotId}
                  onSelectSlot={handleSelectSlot}
                  manualEnabled={manualBookingEnabled}
                  onEnableManual={() => {
                    setManualBookingEnabled(true);
                    setSelectedSlotId(null);
                  }}
                />
              )}

              {/* Provider */}
              <div>
                <label style={labelStyle}>Provider</label>
                <select
                  value={providerId}
                  onChange={(e) => {
                    setProviderId(e.target.value);
                    setSelectedSlotId(null);
                  }}
                  disabled={isLocked || providerScope.scoped}
                  style={selectStyle}
                >
                  <option value="">Select provider...</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name || p.full_name}
                      {p.specialty ? ` — ${p.specialty}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service */}
              <div>
                <label style={labelStyle}>Service</label>
                <select
                  value={serviceId}
                  onChange={(e) => {
                    setServiceId(e.target.value);
                    setSelectedSlotId(null);
                  }}
                  disabled={isLocked}
                  style={selectStyle}
                >
                  <option value="">Select service...</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.category_name ? ` (${s.category_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Duration (read-only) */}
              <div>
                <label style={labelStyle}>Duration</label>
                <div
                  style={{
                    ...inputStyle,
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-surface-2)',
                    color: durationMins ? 'var(--text)' : 'var(--text-muted)',
                  }}
                >
                  {durationMins ? `${durationMins} minutes` : 'Select a service first'}
                </div>
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <select
                  value={statusId}
                  onChange={(e) => setStatusId(e.target.value)}
                  disabled={isLocked}
                  style={selectStyle}
                >
                  <option value="">Select status...</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea
                  placeholder="Add any notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isLocked}
                  rows={3}
                  style={{
                    ...inputStyle,
                    height: 'auto',
                    padding: 'var(--sp-3) var(--input-padding-x)',
                    resize: 'vertical',
                  }}
                />
              </div>

              {conflictMessage && (
                <div
                  role="alert"
                  style={{
                    marginTop: 'var(--sp-4)',
                    padding: 'var(--sp-3) var(--sp-4)',
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#991b1b',
                    fontSize: '0.85rem',
                    lineHeight: 1.45,
                  }}
                >
                  {conflictMessage}
                </div>
              )}

              {/* PRD.299 — WhatsApp booking confirmation opt-out for this booking.
                  Hidden in edit mode: a reschedule never re-confirms. */}
              {!isEditMode && whatsAppBookedConfigured && (
                <div style={{ marginTop: 'var(--sp-4)' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                      cursor: whatsAppToggle.enabled && !isLocked ? 'pointer' : 'default',
                      opacity: whatsAppToggle.enabled ? 1 : 0.65,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sendWhatsAppConfirmation && whatsAppToggle.enabled}
                      disabled={!whatsAppToggle.enabled || isLocked}
                      onChange={(e) => setWhatsAppChoice(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.9rem' }}>Send WhatsApp confirmation</span>
                  </label>

                  {whatsAppToggle.reason && (
                    <div
                      style={{
                        marginTop: 'var(--sp-2)',
                        marginLeft: '1.6rem',
                        fontSize: '0.8rem',
                        color: 'var(--text-muted, #64748b)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>{whatsAppToggleHint(whatsAppToggle.reason)}</span>
                      {/* Only offered for `not-set`. An explicit opt-OUT is never
                          reversed from the booking screen. */}
                      {whatsAppToggle.canOptInInline && patientId && (
                        <button
                          type="button"
                          disabled={optingIn || isLocked}
                          onClick={async () => {
                            if (!db || !orgId || !patientId) return;
                            setOptingIn(true);
                            try {
                              await setWhatsAppOptIn({
                                db,
                                orgId,
                                patientId,
                                optIn: true,
                                existingRow: commPrefRows[0] ?? null,
                              });
                              setWhatsAppChoice(true);
                            } catch (err) {
                              console.error('WhatsApp opt-in failed:', err);
                              setToast('Could not update WhatsApp consent');
                              setToastType('error');
                            } finally {
                              setOptingIn(false);
                            }
                          }}
                          style={{
                            border: '1px solid var(--border)',
                            background: 'transparent',
                            borderRadius: 6,
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                          }}
                        >
                          {optingIn ? 'Saving…' : 'Patient agreed — enable'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Section: Queue ── */}
          {isEditMode && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  marginBottom: 'var(--sp-3)',
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <NavIcon name="list-ordered" />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Queue
                </span>
              </div>
              {isAlreadyQueued ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                    padding: 'var(--sp-3) var(--sp-4)',
                    borderRadius: 'var(--radius-md)',
                    background: '#fef3c7',
                    color: '#92400e',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  <NavIcon name="check" />
                  Added to Queue — Today
                </div>
              ) : (
                <Button variant="outline" size="md" onClick={handleOpenQueueDrawer}>
                  Add to Queue
                </Button>
              )}
            </div>
          )}

          {/* ── Section: Encounter ── */}
          {isEditMode && patientId && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  marginBottom: 'var(--sp-3)',
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <NavIcon name="activity" />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Encounter
                </span>
              </div>
              <Button
                variant="outline"
                size="md"
                onClick={handleStartEncounter}
                disabled={startingEncounter}
              >
                <NavIcon name="activity" />
                {startingEncounter ? 'Starting...' : 'Start Encounter'}
              </Button>
            </div>
          )}

          {/* ── Section: Reminders (PRD.221) ── */}
          {isEditMode && patientId && prefill?.appointmentId && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  marginBottom: 'var(--sp-3)',
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <NavIcon name="message-square" />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Reminders
                </span>
              </div>
              {latestResponse && (
                <div style={{ marginBottom: 'var(--sp-3)' }}>
                  <AppointmentResponseBadge response={latestResponse} size="md" />
                </div>
              )}
              {/* One column, one gap — each action was previously spaced by its
                  own marginTop and the booking-flow button rendered narrow
                  because `fullWidth` was never passed. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                <SendWhatsAppReminderAction
                  presentation="button"
                  appointment={{
                    id: prefill.appointmentId,
                    patientId,
                    patientName: selectedPatient?.full_name ?? 'this patient',
                    date,
                    startTime,
                  }}
                  patientPhone={reminderPhone}
                  whatsAppOptedIn={reminderOptedIn}
                  reminderJob={reminderJob}
                />

                {/* PRD.299 — manual booking confirmation. Covers the cases the
                    automatic send could not: the toggle was off at booking, the
                    patient opted in afterwards, or the endpoint was down. */}
                {whatsAppBookedConfigured && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                    <Button
                      variant="outline"
                      size="md"
                      disabled={!whatsAppToggle.enabled}
                      title={whatsAppToggleHint(whatsAppToggle.reason) ?? undefined}
                      onClick={() => {
                        // Already sent → a resend is a deliberate act, so ask.
                        // It bypasses idempotency and produces a second job.
                        if (
                          bookedJob &&
                          !window.confirm(
                            `A booking confirmation was already sent to ${
                              selectedPatient?.full_name ?? 'this patient'
                            }. Send it again?`,
                          )
                        ) {
                          return;
                        }
                        notifyAppointmentBooked(
                          prefill.appointmentId!,
                          selectedPatient?.full_name ?? 'this patient',
                          { force: Boolean(bookedJob), manual: true },
                        );
                      }}
                    >
                      {bookedJob ? 'Resend Appointment Booked' : 'Send Appointment Booked'}
                    </Button>
                    {(bookedJob || whatsAppToggle.reason) && (
                      <div
                        style={{
                          marginTop: 'var(--sp-2)',
                          fontSize: '0.78rem',
                          color: 'var(--text-muted, #64748b)',
                        }}
                      >
                        {bookedJob
                          ? 'Already sent for this appointment.'
                          : whatsAppToggleHint(whatsAppToggle.reason)}
                      </div>
                    )}
                  </div>
                )}

                <SendWhatsAppFreeFormAction
                  appointment={{
                    id: prefill.appointmentId,
                    patientId,
                    patientName: selectedPatient?.full_name ?? 'this patient',
                  }}
                  // PRD.235 — measured provider delay; undefined → 15 default.
                  runningLateMinutes={providerDelayMinutes}
                  patientPhone={reminderPhone}
                  whatsAppOptedIn={reminderOptedIn}
                />

                <SendWhatsAppBookingFlowAction
                  presentation="button"
                  fullWidth
                  patient={{
                    id: patientId,
                    name: selectedPatient?.full_name ?? 'this patient',
                  }}
                  patientPhone={reminderPhone}
                />
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* Nested Quick Patient Drawer */}
      <QuickPatientDrawer
        open={quickPatientOpen}
        onOpenChange={setQuickPatientOpen}
        brandTokens={brandTokens}
        onCreated={handleQuickPatientCreated}
      />

      {/* Nested Add to Queue Drawer */}
      <AddToQueueDrawer
        open={queueDrawerOpen}
        onOpenChange={setQueueDrawerOpen}
        prefill={queueDrawerPrefill}
        onAdd={handleAddToQueue}
      />
    </>
  );
}

// ─── PRD.234 — Available Slots Section ───

function formatSlotTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  // Slot timestamps are wall-clock-as-UTC; format using UTC accessors so the
  // displayed time matches the clinic's local intent regardless of browser tz.
  return d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

function AvailableSlotsSection({
  slots,
  selectedSlotId,
  onSelectSlot,
  manualEnabled,
  onEnableManual,
}: {
  slots: AppointmentSlotWithBookingRow[];
  selectedSlotId: string | null;
  onSelectSlot: (slot: AppointmentSlotWithBookingRow) => void;
  manualEnabled: boolean;
  onEnableManual: () => void;
}) {
  if (slots.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-4)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
        }}
      >
        <span>No available slots for this provider/service/date.</span>
        {!manualEnabled && (
          <button
            type="button"
            onClick={onEnableManual}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--color-primary)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Book outside available slots
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ opacity: manualEnabled ? 0.7 : 1 }}>
      <label style={{ ...slotsLabelStyle }}>
        Available Slots
        {manualEnabled && (
          <span
            style={{
              marginLeft: 'var(--sp-2)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              textTransform: 'none',
              letterSpacing: 0,
              color: 'var(--text-muted)',
            }}
          >
            (manual entry active — pick a slot to switch back)
          </span>
        )}
      </label>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 'var(--sp-2)',
          marginTop: 'var(--sp-2)',
        }}
      >
        {slots.map((s) => {
          const selected = s.id === selectedSlotId;
          const cap = s.capacity ?? 1;
          const booked = s.booked_count ?? 0;
          const remaining = Math.max(cap - booked, 0);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelectSlot(s)}
              style={{
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: selected
                  ? '2px solid var(--color-primary)'
                  : '1px solid var(--border-input)',
                background: selected ? 'var(--bg-surface-2)' : 'var(--input-bg)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              <div
                style={{
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {formatSlotTime(s.start_time)} – {formatSlotTime(s.end_time)}
              </div>
              <div
                style={{
                  marginTop: 2,
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                {(s.slot_type ?? '').replace('_', ' ').toLowerCase()} · {remaining} slot
                {remaining === 1 ? '' : 's'} left
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <button
          type="button"
          onClick={onEnableManual}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: 'var(--color-primary)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          Book outside available slots
        </button>
      </div>
    </div>
  );
}

const slotsLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
};
